import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../../api/client';
import { Section } from '../../../components/ui/Section';
import { Badge } from '../../../components/ui/Badge';
import { RefreshCw, Clock, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';

interface GcalSyncLogEntry {
  id: string;
  userId: string;
  type: string;
  status: string;
  total: number;
  synced: number;
  created: number;
  updated: number;
  deleted: number;
  errors: string[];
  createdAt: string;
  user: { email: string; name: string };
}

export default function GcalSyncLogSection() {
  const { data: logs = [], isLoading, refetch } = useQuery<GcalSyncLogEntry[]>({
    queryKey: ['gcal-sync-logs'],
    queryFn: () => apiGet<GcalSyncLogEntry[]>('/user-settings/admin/gcal-sync-logs'),
    refetchInterval: 30000,
  });

  const [expandedErrors, setExpandedErrors] = useState<Set<string>>(new Set());

  const toggleErrors = (logId: string) => {
    setExpandedErrors(prev => {
      const next = new Set(prev);
      if (next.has(logId)) next.delete(logId);
      else next.add(logId);
      return next;
    });
  };

  const statusBadge = (status: string) => {
    switch (status) {
      case 'SUCCESS': return <Badge variant="success">OK</Badge>;
      case 'PARTIAL': return <Badge variant="warning">Parziale</Badge>;
      case 'ERROR':  return <Badge variant="error">Errore</Badge>;
      default:       return <Badge>{status}</Badge>;
    }
  };

  const typeLabel = (type: string) => type === 'STUDIO' ? 'Studio' : type === 'ALL' ? 'Globale' : 'Personale';

  return (
    <Section title="Log Sincronizzazione Calendario" action={
      <button onClick={() => refetch()} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)' }}>
        <RefreshCw size={14} />
      </button>
    }>
      {isLoading ? (
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>Caricamento...</p>
      ) : logs.length === 0 ? (
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
          <Clock size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          Nessuna sincronizzazione registrata
        </p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-xs)' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.10)' }}>
                <th style={thStyle}>Data/Ora</th>
                <th style={thStyle}>Utente</th>
                <th style={thStyle}>Tipo</th>
                <th style={thStyle}>Stato</th>
                <th style={thStyle}>Creati</th>
                <th style={thStyle}>Agg.</th>
                <th style={thStyle}>Elim.</th>
                <th style={thStyle}>Errori</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => {
                const hasErrors = log.errors.length > 0;
                const isExpanded = expandedErrors.has(log.id);
                return (
                  <>
                    <tr key={log.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                      <td style={tdStyle}>{new Date(log.createdAt).toLocaleString('it-IT')}</td>
                      <td style={tdStyle}>{log.user?.email ?? log.userId}</td>
                      <td style={tdStyle}>{typeLabel(log.type)}</td>
                      <td style={tdStyle}>{statusBadge(log.status)}</td>
                      <td style={{ ...tdStyle, color: 'var(--color-success)' }}>{log.created}</td>
                      <td style={{ ...tdStyle, color: 'var(--color-primary)' }}>{log.updated}</td>
                      <td style={tdStyle}>{log.deleted ?? 0}</td>
                      <td style={tdStyle}>
                        {hasErrors ? (
                          <button
                            onClick={() => toggleErrors(log.id)}
                            title="Clicca per vedere i dettagli"
                            style={{
                              ...errorButtonStyle,
                              color: log.status === 'ERROR' ? 'var(--color-error)' : 'var(--color-warning-text)',
                            }}
                          >
                            <AlertTriangle size={11} style={{ marginRight: 3 }} />
                            {log.errors.length}
                            {isExpanded ? <ChevronUp size={11} style={{ marginLeft: 3 }} /> : <ChevronDown size={11} style={{ marginLeft: 3 }} />}
                          </button>
                        ) : '—'}
                      </td>
                    </tr>
                    {isExpanded && hasErrors && (
                      <tr key={`${log.id}-errors`}>
                        <td colSpan={8} style={{ padding: '0.5rem 0.75rem', background: 'var(--glass-bg-card)' }}>
                          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-error)', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                            {log.errors.map((err, i) => (
                              <div key={i} style={{ padding: '0.25rem 0.5rem', background: 'var(--glass-bg-card)', borderRadius: 'var(--radius-interactive)', border: '1px solid rgba(255,255,255,0.08)' }}>
                                {err}
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

const thStyle: React.CSSProperties = { textAlign: 'left', padding: '0.5rem 0.75rem', fontWeight: 600, color: 'var(--color-text-muted)', whiteSpace: 'nowrap' };
const tdStyle: React.CSSProperties = { padding: '0.375rem 0.75rem', whiteSpace: 'nowrap' };
const errorButtonStyle: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer', padding: '0.125rem 0.375rem',
  display: 'inline-flex', alignItems: 'center', gap: '0.125rem',
  borderRadius: 'var(--radius-interactive)', fontSize: 'var(--text-xs)',
  fontWeight: 500,
};
